/**
 * "tapIt": elements placed freely on the slide; tap the correct ones
 * (PlayspaceTapIt.cs / TapElementPlayer.cs). Items = number of correct
 * elements (or 1 when selectAllCorrects is off). Elements without image and
 * without text are invisible hot-spots.
 */
import { useState } from "react";
import type { SlideOf, TapElement } from "@makeit/schema";
import { layoutStyle } from "../Stage";
import { ElementImage, TextBox, useElementSound } from "../elements";
import { useActivity } from "../activity";

export function TapItSlide({ slide }: { slide: SlideOf<"tapIt"> }) {
  const correctCount = slide.tapElements.filter((e) => e.correct).length;
  const activity = useActivity(slide.selectAllCorrects ? Math.max(1, correctCount) : 1);
  const [done, setDone] = useState<Record<string, "correct" | "wrong">>({});

  const tap = (e: TapElement, play: () => void) => {
    if (done[e.id] || activity.isDone()) return;
    setDone((d) => ({ ...d, [e.id]: e.correct ? "correct" : "wrong" }));
    if (e.correct) {
      play();
      activity.correct(e.text.content);
    } else {
      activity.wrong(slide.alwaysCorrect, e.text.content);
    }
  };

  return (
    <>
      {slide.tapElements.map((e) => (
        <TapTarget key={e.id} element={e} state={done[e.id]} onTap={tap} />
      ))}
    </>
  );
}

function TapTarget({ element, state, onTap }: { element: TapElement; state?: "correct" | "wrong"; onTap: (e: TapElement, play: () => void) => void }) {
  const { play } = useElementSound(element.correct ? element : null);
  const invisible = !element.image && !element.text.content;
  return (
    <div
      className={`tap-target ${invisible ? "invisible" : ""} ${state ?? ""}`}
      data-id={element.id}
      style={layoutStyle(element.layout)}
      onClick={() => onTap(element, play)}
    >
      {element.image && <ElementImage element={element} />}
      <TextBox text={element.text} style={{ position: "absolute", inset: 0 }} bestFit={300} />
      {state && <span className={`mark ${state}`}>{state === "correct" ? "✓" : "✗"}</span>}
    </div>
  );
}
